import { Reducer } from 'redux';
import {
  ACTION,
  ClearList, RequestList, ReceiveList, FailList,
  RequestDelete, ReceiveDelete, FailDelete,
  RequestPersonAccess, ReceivePersonAccess, FailPersonAccess, ClearPersonAccess,
  RequestUserExportExcel, ReceiveUserExportExcel, FailUserExportExcel,
  RequestRoleExportExcel, ReceiveRoleExportExcel, FailRoleExportExcel,
  RequestDefaultRole, ReceiveDefaultRole, FailDefaultRole,
  RequestSetDefaultRole, ReceiveSetDefaultRole, FailSetDefaultRole,
  RequestExtraInfo, ReceiveExtraInfo, FailExtraInfo,
  RequestCheckRoles, ReceiveCheckRoles, FailCheckRoles
} from './action-types';
import { AdministrationSearchState } from './types-store';
import { STATE } from 'app/constants';
import { withOptionsReducer } from 'app/components/options-handler';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

export const INITIAL_ADMINISTRATION_LIST_STATE: AdministrationSearchState = {
  results: {
    list: [],
    itemPersonAccess: {
      fetching: false
    },
    total: 0,
    fetching: false,
    requestId: 0
  },
  requestedSearchModel: undefined,
  defaultRole: undefined,
  exportUserExcelFetching: false,
  exportRoleExcelFetching: false,
  fetchingDefaultRole: false,
  isSettingDefaultRole: false,
  settingDefaultRoleId: undefined,
  extraInfoCodes: [],
  fetchingExtraInfoCodes: false,
  hasRoles: false,
  fetchingRoles: false
};

const reducers: { [indexer: string]: Reducer<AdministrationSearchState> } = {

  [ACTION.CLEAR_LIST]: (state: AdministrationSearchState, action: ClearList): AdministrationSearchState => {

    return {
      ...state,
      results: {
        list: [],
        total: undefined,
        fetching: false,
        requestId: action.requestId
      },
      requestedSearchModel: undefined
    };
  },

  [ACTION.REQUEST_PERSON_ACCESS]: (state: AdministrationSearchState, action: RequestPersonAccess): AdministrationSearchState => {
    return {
      ...state,
      results: {
        ...state.results,
        itemPersonAccess: {
          fetching: true
        }
      }
    };
  },

  [ACTION.RECEIVE_PERSON_ACCESS]: (state: AdministrationSearchState, action: ReceivePersonAccess): AdministrationSearchState => {
    return {
      ...state,
      results: {
        ...state.results,
        itemPersonAccess: {
          access: action.access,
          fetching: false
        }
      }
    };
  },

  [ACTION.FAIL_PERSON_ACCESS]: (state: AdministrationSearchState, action: FailPersonAccess): AdministrationSearchState => {
    return {
      ...state,
      results: {
        ...state.results,
        itemPersonAccess: {
          fetching: false
        }
      }
    };
  },

  [ACTION.CLEAR_PERSON_ACCESS]: (state: AdministrationSearchState, action: ClearPersonAccess): AdministrationSearchState => {
    return {
      ...state,
      results: {
        ...state.results,
        itemPersonAccess: undefined
      }
    };
  },

  [ACTION.REQUEST_LIST]: (state: AdministrationSearchState, action: RequestList): AdministrationSearchState => {

    return {
      ...state,
      results: {
        ...state.results,
        ...(action.clearResults && {
          list: [],
          total: undefined
        }),
        fetching: true,
        requestId: action.requestId
      },
      requestedSearchModel: action.model
    };
  },

  [ACTION.RECEIVE_LIST]: (state: AdministrationSearchState, action: ReceiveList): AdministrationSearchState => {

    if (action.requestId !== state.results.requestId) {
      return state;
    }

    return {
      ...state,
      results: {
        ...state.results,
        fetching: false,
        list: [
          ...state.results.list,
          ...action.list
        ],
        total: action.total
      }
    };
  },

  [ACTION.FAIL_LIST]: (state: AdministrationSearchState, action: FailList): AdministrationSearchState => {

    if (action.requestId !== state.results.requestId) {
      return state;
    }

    return {
      ...state,
      results: {
        ...state.results,
        fetching: false
      }
    };
  },

  [ACTION.REQUEST_DELETE]: (state: AdministrationSearchState, action: RequestDelete): AdministrationSearchState => {

    return {
      ...state,
      results: {
        ...state.results,
        list: state.results.list.map(item => item.id === action.id
          ? {
            ...item,
            deleted: true
          }
          : item)
      }
    };
  },

  [ACTION.RECEIVE_DELETE]: (state: AdministrationSearchState, action: ReceiveDelete): AdministrationSearchState => {

    return {
      ...state,
      results: {
        ...state.results,
        list: state.results.list.filter(item => item.id !== action.id),
        total: state.results.total - 1
      }
    };
  },

  [ACTION.FAIL_DELETE]: (state: AdministrationSearchState, action: FailDelete): AdministrationSearchState => {

    return {
      ...state,
      results: {
        ...state.results,
        list: state.results.list.map(item => item.id === action.id
          ? {
            ...item,
            deleted: undefined
          }
          : item)
      }
    };
  },

  [ACTION.REQUEST_USER_EXPORT_EXCEL]: (state: AdministrationSearchState, action: RequestUserExportExcel): AdministrationSearchState => {

    return {
      ...state,
      exportUserExcelFetching: true
    };
  },

  [ACTION.RECEIVE_USER_EXPORT_EXCEL]: (state: AdministrationSearchState, action: ReceiveUserExportExcel): AdministrationSearchState => {

    return {
      ...state,
      exportUserExcelFetching: false
    };
  },

  [ACTION.FAIL_USER_EXPORT_EXCEL]: (state: AdministrationSearchState, action: FailUserExportExcel): AdministrationSearchState => {

    return {
      ...state,
      exportUserExcelFetching: false
    };
  },

  [ACTION.REQUEST_ROLE_EXPORT_EXCEL]: (state: AdministrationSearchState, action: RequestRoleExportExcel): AdministrationSearchState => {

    return {
      ...state,
      exportRoleExcelFetching: true
    };
  },

  [ACTION.RECEIVE_ROLE_EXPORT_EXCEL]: (state: AdministrationSearchState, action: ReceiveRoleExportExcel): AdministrationSearchState => {

    return {
      ...state,
      exportRoleExcelFetching: false
    };
  },

  [ACTION.FAIL_ROLE_EXPORT_EXCEL]: (state: AdministrationSearchState, action: FailRoleExportExcel): AdministrationSearchState => {

    return {
      ...state,
      exportRoleExcelFetching: false
    };
  },

  [ACTION.REQUEST_DEFAULT_ROLE]: (state: AdministrationSearchState, action: RequestDefaultRole): AdministrationSearchState => {
    return {
      ...state,
      fetchingDefaultRole: true
    };
  },

  [ACTION.RECEIVE_DEFAULT_ROLE]: (state: AdministrationSearchState, action: ReceiveDefaultRole): AdministrationSearchState => {
    return {
      ...state,
      fetchingDefaultRole: false,
      defaultRole: action.defaultRole
    };
  },

  [ACTION.FAIL_DEFAULT_ROLE]: (state: AdministrationSearchState, action: FailDefaultRole): AdministrationSearchState => {
    return {
      ...state,
      fetchingDefaultRole: false
    };
  },

  [ACTION.REQUEST_SET_DEFAULT_ROLE]: (state: AdministrationSearchState, action: RequestSetDefaultRole): AdministrationSearchState => {
    return {
      ...state,
      isSettingDefaultRole: true,
      settingDefaultRoleId: isDefinedIncludingZero(action.settingDefaultRoleId) ? action.settingDefaultRoleId : state.defaultRole?.value
    };
  },

  [ACTION.RECEIVE_SET_DEFAULT_ROLE]: (state: AdministrationSearchState, action: ReceiveSetDefaultRole): AdministrationSearchState => {
    return {
      ...state,
      isSettingDefaultRole: false,
      settingDefaultRoleId: undefined,
      defaultRole: { value: state.settingDefaultRoleId }
    };
  },

  [ACTION.FAIL_SET_DEFAULT_ROLE]: (state: AdministrationSearchState, action: FailSetDefaultRole): AdministrationSearchState => {
    return {
      ...state,
      isSettingDefaultRole: false,
      settingDefaultRoleId: undefined
    };
  },

  [ACTION.REQUEST_EXTRA_INFO]: (state: AdministrationSearchState, action: RequestExtraInfo): AdministrationSearchState => {
    return {
      ...state,
      extraInfoCodes: [],
      fetchingExtraInfoCodes: true
    };
  },

  [ACTION.RECEIVE_EXTRA_INFO]: (state: AdministrationSearchState, action: ReceiveExtraInfo): AdministrationSearchState => {
    return {
      ...state,
      extraInfoCodes: action.extraInfoCodes ?? [],
      fetchingExtraInfoCodes: false
    };
  },

  [ACTION.FAIL_EXTRA_INFO]: (state: AdministrationSearchState, action: FailExtraInfo): AdministrationSearchState => {
    return {
      ...state,
      extraInfoCodes: [],
      fetchingExtraInfoCodes: false
    };
  },

  [ACTION.REQUEST_CHECK_ROLES]: (state: AdministrationSearchState, action: RequestCheckRoles): AdministrationSearchState => {

    return {
      ...state,
      fetchingRoles: true
    };
  },

  [ACTION.RECEIVE_CHECK_ROLES]: (state: AdministrationSearchState, action: ReceiveCheckRoles): AdministrationSearchState => {

    return {
      ...state,
      hasRoles: action.hasRoles,
      fetchingRoles: false
    };
  },

  [ACTION.FAIL_CHECK_ROLES]: (state: AdministrationSearchState, action: FailCheckRoles): AdministrationSearchState => {

    return {
      ...state,
      fetchingRoles: false
    };
  }
};

const stateReducer: Reducer<AdministrationSearchState> =
  (state: AdministrationSearchState = INITIAL_ADMINISTRATION_LIST_STATE, action: any) => {
    const r = reducers[action.type];
    return (r && r(state, action)) || state;
  };

export const reducer = withOptionsReducer(STATE.administrationSearch, stateReducer);
